// CallingCodeSelector Component
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import phoneCodes from "../../PhoneInput/newcoutries.json";
import useClickOutside from "hooks/useClickOutside";
import { useLang } from "hooks/useLang";

interface ICallingCode {
  code: string;
  country: string;
  label: string;
  value: string;
}

interface IProps {
  setCallingCode: (callingCode: string) => void;
  callingCode: string;
  disable?: boolean;
}

const CallingCodeSelector = ({
  setCallingCode,
  callingCode,
  disable,
}: IProps) => {
  const { t } = useLang();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [filterInput, setFilterInput] = useState<string>("");
  const [filteredCodes, setFilteredCodes] = useState<ICallingCode[]>(
    Object.values(phoneCodes)
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const initialSelectedCode = useMemo(() => {
    return (
      Object.values(phoneCodes).find((code) => code.value === callingCode) ||
      phoneCodes.SA
    );
  }, [callingCode]);

  const [selectedCode, setSelectedCode] =
    useState<ICallingCode>(initialSelectedCode);

  useClickOutside(dropdownRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    if (filterInput) {
      const filtered = Object.values(phoneCodes).filter((country) =>
        country.value.toLowerCase().includes(filterInput.toLowerCase())
      );
      setFilteredCodes(filtered);
    } else {
      setFilteredCodes(Object.values(phoneCodes));
    }
  }, [filterInput]);


  const handleCallingCode = (country: ICallingCode) => {
    setSelectedCode(country);
    setCallingCode(country.value);
    setIsOpen(false);
    setFilterInput("");
  };

  const toggleDropdown = () => {
    setIsOpen((prevState) => !prevState);
  };

  const searchCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(e.target.value))) {
      return;
    }

    setFilterInput(e.target.value);
  };

  const style = useMemo(() => {
    return {
      container: `flex items-center z-10 cursor-pointer rounded-md p-1 transition-all select-none duration-200 md:hover:bg-gray-200 md:hover:dark:bg-primary-theme-400 ${isOpen
        ? "w-[130px] bg-gray-200 dark:bg-primary-theme-400"
        : "w-[100px] bg-transparent"
        } ${disable ? "pointer-events-none" : ""}`,
      selected: "flex h-full w-full items-center justify-center px-2 py-2",
      dropdownMenu:
        " absolute max-h-[200px] w-[27.5%] overflow-auto rounded-md bg-gray-100 dark:bg-primary-theme-400 shadow-md w-[50%]",

      dropdownItem:
        "flex items-center py-2 pl-4 md:hover:dark:bg-primary-theme-200 md:hover:bg-gray-200",
      searchInput:
        "sticky top-0 w-full bg-gray-100 p-1 text-xs placeholder:text-xs dark:bg-primary-theme-200",
    };
  }, [isOpen, disable]);


  return (
    <div className={style.container} ref={dropdownRef}>
      <div className={style.selected} onClick={toggleDropdown}>
        <div className="w-5">
          <span
            dangerouslySetInnerHTML={{
              __html: selectedCode.label,
            }}
          />
        </div>
        <span className="px-2 text-xs">{selectedCode.value}</span>
      </div>
      <AnimatePresence>
        {isOpen ? (
          <div
            className={style.dropdownMenu}
          >
            <input
              type="text"
              value={filterInput}
              placeholder={t("btn_search")}
              autoFocus
              inputMode="numeric"
              onChange={searchCode}
              maxLength={6}
              className={style.searchInput}
            />


            {filteredCodes.map((country) => (
              <div
                key={`${country.code} ${country.value}`}
                className={style.dropdownItem}
                onClick={() => handleCallingCode(country)}
              >
                <div className="w-5">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: country.label,
                    }}
                  />
                </div>
                <span className="px-2 text-xs text-black dark:text-white">
                  {country.value}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default CallingCodeSelector;
